import React, {useEffect, useState} from 'react';
import {Button, Form, Input, message, Typography} from 'antd';
import moment from 'moment';
import {CollectionWorkProjectType, ICollectionWork} from '../../hooks/apis/useCollectionWork';
import useCollectionWorkProjectRecord from '../../hooks/apis/useCollectionWorkProjectRecord';
import UseCollectionWorkProjectRecord, {CollectionWorkProjectStatusConfig, IFileRecord} from '../../hooks/apis/useCollectionWorkProjectRecord';
import CollectionWorkProjectSelector from './Selector';
import UploadButton from '../UploadButton';
import {useBoolean} from 'react-hanger';
import UseCollectionWorkProject from '../../hooks/apis/useCollectionWorkProject';
import CommonApiErrorHandler from '../../utils/HttpInstance';
import {useAxios} from '../../utils/AxiosProvider';

export interface ICollectionWorkProjectSubmitForm {
  name: string;
  collectionWorkProjectId: number;
  fileRecordId: number;
  id: number;
}
const CollectionWorkProjectSubmitForm = (props: {collectionWork: ICollectionWork; onFinish?: () => any}) => {
  const [form] = Form.useForm();
  const loading = useBoolean(false);
  const [file, setFile] = useState<IFileRecord>();
  const [projectId, setProjectId] = useState<number>();
  const [recordId, setRecordId] = useState<number>();
  const defaultForm: Partial<ICollectionWorkProjectSubmitForm> = {};
  const submitHook = useCollectionWorkProjectRecord.save();
  const queryByProjectIdHook = UseCollectionWorkProjectRecord.queryByProjectId({
    errorHandler: CommonApiErrorHandler,
  });
  const projectDetailHook = UseCollectionWorkProject.queryById();
  const axios = useAxios();
  useEffect(() => {
    if (projectId) {
      queryByProjectIdHook
        .request({
          projectId: projectId,
        })
        .then(i => {
          setRecordId(i.id);
          form.setFieldsValue({
            name: i.name,
            collectionWorkProjectId: i.collectionWorkProjectId,
            fileRecordId: i.fileRecordId,
            id: i.id,
          });
          setFile(i.fileRecord);
        })
        .catch(e => {
          setRecordId(undefined);
          form.resetFields();
          form.setFieldsValue({
            ...defaultForm,
            collectionWorkProjectId: projectId,
          });
          setFile(undefined);
        });
      projectDetailHook
        .request({
          id: projectId,
        })
        .then()
        .catch(CommonApiErrorHandler);
    }
  }, [projectId]);

  function onFinish(form1: ICollectionWorkProjectSubmitForm) {
    submitHook
      .request({
        collectionWorkProjectId: form1.collectionWorkProjectId,
        name: form1.name,
        id: recordId,
        fileRecordId: form1.fileRecordId,
      })
      .then(i => {
        message.success({
          content: '成功提交作品',
        });
        form.resetFields();
        setProjectId(undefined);
        setFile(undefined);
        props.onFinish && props.onFinish();
      })
      .catch(CommonApiErrorHandler);
  }
  function uploadFile(formData: FormData) {
    const form1 = form.getFieldsValue();
    formData.append('collectionWorkProjectId', form1.collectionWorkProjectId);
    loading.setTrue();
    axios.instance
      .post<IFileRecord>('fileRecord/upload', formData, {})
      .then(res => {
        setFile(res.data);
        form.setFieldsValue({
          fileRecordId: res.data.id,
        });
      })
      .catch(e => {
        message.error(e.message);
      })
      .finally(() => {
        loading.setFalse();
      });
  }

  const limitSuffix = projectDetailHook.data?.type === CollectionWorkProjectType.文档 ? ['.docx', '.doc', '.pdf'] : ['.png', '.jpeg', '.jpg'];

  let isAllowUpload = false;
  if (props.collectionWork.statusConfig === CollectionWorkProjectStatusConfig.关闭) {
    isAllowUpload = false;
  } else if (props.collectionWork.statusConfig === CollectionWorkProjectStatusConfig.开启) {
    isAllowUpload = true;
  } else if (props.collectionWork.statusConfig === CollectionWorkProjectStatusConfig.自动) {
    const beginMoment = moment(props.collectionWork.beginTime);
    const endMoment = moment(props.collectionWork.endTime);
    isAllowUpload = moment().isBefore(endMoment) && moment().isAfter(beginMoment);
  }
  return (
    <Form
      labelCol={{span: 6}}
      wrapperCol={{span: 14}}
      layout={'horizontal'}
      form={form}
      onFinish={value => {
        onFinish(value as ICollectionWorkProjectSubmitForm);
      }}>
      <Form.Item label={'参评项目'} name={'collectionWorkProjectId'} rules={[{required: true}]}>
        <CollectionWorkProjectSelector size="large" collectionWorkId={props.collectionWork?.id} onChange={i => setProjectId(i)} />
      </Form.Item>
      <Form.Item label={'作品名称'} name={'name'} rules={[{required: true}]}>
        <Input size={'large'} placeholder="请输入作品名称" />
      </Form.Item>
      {projectId && (
        <React.Fragment>
          <Form.Item label={'选择文件'} name={'fileRecordId'} rules={[{required: true}]}>
            <Typography.Link href={file?.url} style={{display: 'block'}}>
              {file?.name}
            </Typography.Link>
            <UploadButton
              maxSize={projectDetailHook.data?.fileSizeMb}
              disabled={!isAllowUpload}
              accept={limitSuffix.join(',')}
              id={'UploadFile'}
              loading={loading.value}
              upload={uploadFile}
            />
            <Typography.Text type={'secondary'}>
              注意事项：仅支持{' '}
              {limitSuffix
                .join('、')
                .replaceAll('application/', '')
                .replaceAll('image/', '')}{' '}
              格式，大小限制为 {projectDetailHook.data?.fileSizeMb} MB。
            </Typography.Text>
            {projectDetailHook.data && (
              <>
                {props.collectionWork.statusConfig === CollectionWorkProjectStatusConfig.自动 && (
                  <Typography.Text type={'secondary'} style={{display: 'block'}}>
                    上传时间：{props.collectionWork.beginTime} ~ {props.collectionWork.endTime}
                  </Typography.Text>
                )}
                {!isAllowUpload && (
                  <Typography.Text type={'secondary'} style={{display: 'block'}}>
                    通道已关闭
                  </Typography.Text>
                )}
              </>
            )}
          </Form.Item>
          <Form.Item label={'操作'} rules={[{required: true}]}>
            <Button disabled={!isAllowUpload} type={'primary'} htmlType={'submit'} loading={submitHook.loading}>
              提交
            </Button>
          </Form.Item>
        </React.Fragment>
      )}
    </Form>
  );
};
export default CollectionWorkProjectSubmitForm;
